// Guided views / story playback — wave-2 system map (G4). One chrome
// controller (identifier `ui--flow-story`) rendering a chapter rail INSIDE a
// PanelComponent composition, in the spirit of the PR #57 palette filter:
// a dedicated controller with one responsibility, driven entirely by public
// engine APIs (getNode/getEdges/selectNode/setCenter/setViewportAnimated).
//
// Chapters come from the canvas root's data-ui--flow-views-value JSON (the
// CanvasComponent `views:` kwarg — validated server-side, fail-closed). The
// engine declares no `views` value, so the attribute is inert to
// flow_controller; this controller is its only consumer.
//
// ARCHIFY GROUNDING RULE: transition classification between consecutive
// chapters reads ONLY flow.getEdges() authored defs (source/target keys) —
// never DOM geometry, never transitive inference. A chapter pair with no
// direct authored edge in either direction is "group" (·) — grouped, never
// computed. No invented topology.
//
// URL/hash ownership split (one writer per param — mirror of the header in
// flow_url_state_controller.js):
//   #view= / #beat=   THIS controller (chapter + beat node, replaceState only)
//   ?present= / #focus=   flow_url_state_controller.js (G5)
//   #route= / #lens=      flow_inspect_controller.js (INSPECT track)
// Writers touch their own params only; every other hash param survives a
// write untouched. replaceState, never pushState — viewer state is
// replace-state, not history spam (archify ruling carried into wave 2).
import { Controller } from "@hotwired/stimulus"
import { resolveFlowEngine } from "../../../lib/flow/utils.js"

// Chapter camera: the engine has no {nodes: [...]} fitView focus variant, so
// a chapter frames its focus set via setCenter on the centroid at this zoom.
const FOCUS_ZOOM = 1.2
const VIEWPORT_ANIMATION_MS = 400

// Playback cadence: 3200ms per step with an 1100ms floor — a host
// step-interval override can tighten the pace but never below the floor.
const DEFAULT_STEP_INTERVAL_MS = 3200
const STEP_INTERVAL_FLOOR_MS = 1100

// Transition kinds between consecutive chapters' focus sets. start = the
// first chapter (no incoming edge pair to classify); forward/reverse =
// a direct authored edge in that direction only; multiple = both directions;
// group = NO direct edge — grouped, never computed (the grounding rule).
const SEPARATORS = { forward: "→", reverse: "←", multiple: "⇄", group: "·" }

// Chapter-button chrome. These exact tokens also appear in the preview ERB
// (Tailwind sweeps components/previews, NOT app/javascript) — a token that
// only existed here would silently miss the stylesheet.
const CHAPTER_CLASSES = "rounded border border-border bg-card px-2 py-1 text-2xs font-medium text-foreground-medium hover:bg-muted hover:text-foreground"
const CHAPTER_ACTIVE_REMOVE = ["bg-card", "text-foreground-medium", "border-border"]
const CHAPTER_ACTIVE_ADD = ["bg-primary/10", "text-primary", "border-primary/60"]

export default class extends Controller {
  // `list` = the <ol> the rail renders into; `note` = the active chapter's
  // prose; `position` = the "2 / 5" readout; `progress` = the bar fill;
  // `playButton` = the play/pause toggle; `beat` = the beat-node readout;
  // `status` = the copy-link feedback line; `copyButton` = the copy-moment-
  // link button (the markup declares the target — declaring it here keeps
  // the contract honest; the action wires copyMoment).
  static targets = ["list", "note", "position", "progress", "playButton", "beat", "status", "copyButton"]

  // The engine is resolved through the widget outlet attribute
  // (data-ui--flow-story-ui--flow-outlet="#<canvas id>") like every other
  // Flow::* satellite — resolveFlowEngine falls back to the id lookup.
  static outlets = ["ui--flow"]

  static values = {
    // Host playback-pace override (ms per step); floored at 1100ms.
    stepInterval: { type: Number, default: DEFAULT_STEP_INTERVAL_MS }
  }

  connect() {
    this._flow = null
    this._views = []
    this._index = 0
    this._playing = false
    this._timer = null
    this._beatNodeId = null

    this._onSelectionChange = (event) => this._handleSelectionChange(event)
    // A hidden tab must not keep stepping a story nobody is watching.
    this._onVisibilityChange = () => { if (document.hidden) this.pause() }
    document.addEventListener("ui--flow:selectionchange", this._onSelectionChange)
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    this._boot()
  }

  disconnect() {
    this._clearTimer()
    document.removeEventListener("ui--flow:selectionchange", this._onSelectionChange)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    if (this._retryTimer) clearTimeout(this._retryTimer)
  }

  // Engine resolution is attribute-driven; one short retry covers host
  // markup with the identifier but a slow Stimulus upgrade — the same
  // no-permanent-polling pattern status_bar_controller uses (M4).
  _boot() {
    const flow = resolveFlowEngine(this)
    if (flow) { this._attach(flow); return }
    this._retryTimer = setTimeout(() => {
      const retry = resolveFlowEngine(this)
      if (retry) this._attach(retry)
    }, 500)
  }

  _attach(flow) {
    this._flow = flow
    this._views = this._readViews()
    this._renderRail()
    // The initial render covers the empty-story case too ("0 / 0") —
    // _goToChapter's out-of-range guard never reaches _renderState for it.
    this._renderState()
    this._bootFromHash()
  }

  // The canvas root carries the chapters as JSON (CanvasComponent `views:`).
  // A missing/malformed attribute degrades to an empty story, never a crash.
  _readViews() {
    const raw = this._flow.element.getAttribute("data-ui--flow-views-value")
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  // ── Rail rendering ──────────────────────────────────────

  _renderRail() {
    if (!this.hasListTarget) return
    this.listTarget.textContent = ""
    this._views.forEach((view, index) => {
      const item = document.createElement("li")
      item.className = "flex items-center gap-1"
      if (index > 0) {
        const separator = document.createElement("span")
        separator.className = "text-2xs text-foreground-medium select-none"
        separator.setAttribute("aria-hidden", "true")
        separator.textContent = SEPARATORS[this._classify(this._views[index - 1].focus, view.focus)]
        item.appendChild(separator)
      }
      item.appendChild(this._chapterButton(view, index))
      this.listTarget.appendChild(item)
    })
  }

  _chapterButton(view, index) {
    const button = document.createElement("button")
    button.type = "button"
    button.textContent = view.label
    button.className = CHAPTER_CLASSES
    button.dataset.action = "ui--flow-story#selectChapter"
    // Stimulus action param: data-ui--flow-story-index-param (tree_controller
    // uses the same -param pattern). setAttribute, not a dataset key — the
    // dashes are not a valid identifier.
    button.setAttribute("data-ui--flow-story-index-param", `${index}`)
    button.setAttribute("aria-pressed", "false")
    return button
  }

  // Transition classification between consecutive chapters' focus sets —
  // AUTHORED EDGES ONLY (flow.getEdges() defs): a direct edge prev→next is
  // "forward", next→prev is "reverse", both is "multiple", neither is
  // "group". No BFS, no geometry, no transitive inference.
  _classify(prevFocus, nextFocus) {
    const prev = new Set(prevFocus)
    const next = new Set(nextFocus)
    let forward = false
    let reverse = false
    for (const edge of this._flow.getEdges()) {
      if (prev.has(edge.source) && next.has(edge.target)) forward = true
      if (next.has(edge.source) && prev.has(edge.target)) reverse = true
      if (forward && reverse) break
    }
    if (forward && reverse) return "multiple"
    if (forward) return "forward"
    if (reverse) return "reverse"
    return "group"
  }

  _renderState() {
    const count = this._views.length
    const buttons = this.hasListTarget ? this.listTarget.querySelectorAll("button") : []
    buttons.forEach((button, index) => {
      const active = index === this._index
      button.setAttribute("aria-pressed", active ? "true" : "false")
      if (active) {
        button.classList.remove(...CHAPTER_ACTIVE_REMOVE)
        button.classList.add(...CHAPTER_ACTIVE_ADD)
      } else {
        button.classList.remove(...CHAPTER_ACTIVE_ADD)
        button.classList.add(...CHAPTER_ACTIVE_REMOVE)
      }
    })

    const view = this._views[this._index]
    if (this.hasNoteTarget) this.noteTarget.textContent = view?.note || ""
    if (this.hasPositionTarget) this.positionTarget.textContent = count === 0 ? "0 / 0" : `${this._index + 1} / ${count}`
    if (this.hasProgressTarget) this.progressTarget.style.width = count === 0 ? "0%" : `${Math.round(((this._index + 1) / count) * 100)}%`
    if (this.hasBeatTarget) this._renderBeat()
    this._renderPlayback()
  }

  _renderPlayback() {
    if (!this.hasPlayButtonTarget) return
    this.playButtonTarget.textContent = this._playing ? "Pause" : "Play"
    this.playButtonTarget.setAttribute("aria-pressed", this._playing ? "true" : "false")
  }

  _renderBeat() {
    if (!this.hasBeatTarget) return
    if (!this._beatNodeId) { this.beatTarget.textContent = ""; return }
    const def = this._flow?.getNode(this._beatNodeId)
    this.beatTarget.textContent = `Beat: ${def?.label || this._beatNodeId}`
  }

  // ── Chapter navigation ──────────────────────────────────

  selectChapter(event) {
    // A manual step pauses playback — the user is driving now.
    this.pause()
    this._goToChapter(Number(event.params.index))
  }

  next() {
    this.pause()
    this._goToChapter(this._index + 1)
  }

  prev() {
    this.pause()
    this._goToChapter(this._index - 1)
  }

  _goToChapter(index, { animate = true } = {}) {
    if (!this._flow || index < 0 || index >= this._views.length) return
    this._index = index
    const view = this._views[index]

    const centroid = this._focusCentroid(view.focus)
    if (centroid) {
      if (animate) {
        this._flow.setViewportAnimated(this._centerViewport(centroid.x, centroid.y, FOCUS_ZOOM), VIEWPORT_ANIMATION_MS)
      } else {
        this._flow.setCenter(centroid.x, centroid.y, FOCUS_ZOOM)
      }
    }

    // A chapter change resets the beat — the moment is the new chapter plus
    // whatever node the user picks next.
    this._beatNodeId = null
    this._renderState()
    this._writeHash()
  }

  // Mean of the focus nodes' box centers (def positions + default 150×80
  // boxes, mirroring getNodesBounds' fallbacks). Unknown ids are skipped —
  // server-side validation guarantees refs exist, but the graph can change
  // at runtime (a deleted node must not break the rail).
  _focusCentroid(focusIds) {
    const centers = []
    for (const id of focusIds || []) {
      const def = this._flow.getNode(id)
      if (!def || !def.position) continue
      centers.push({ x: def.position.x + (def.width || 150) / 2, y: def.position.y + (def.height || 80) / 2 })
    }
    if (centers.length === 0) return null
    const sum = centers.reduce((acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }), { x: 0, y: 0 })
    return { x: sum.x / centers.length, y: sum.y / centers.length }
  }

  // setCenter's exact viewport math (flow_controller.js:701) as a target for
  // setViewportAnimated — the smooth chapter transition.
  _centerViewport(cx, cy, zoom) {
    const rect = this._flow.element.getBoundingClientRect()
    return { x: rect.width / 2 - cx * zoom, y: rect.height / 2 - cy * zoom, zoom }
  }

  // ── Playback ────────────────────────────────────────────

  togglePlay() {
    this._playing ? this.pause() : this.play()
  }

  play() {
    if (this._playing || this._views.length < 2) return
    this._playing = true
    this._renderPlayback()
    this._scheduleStep()
  }

  pause() {
    if (!this._playing && !this._timer) return
    this._playing = false
    this._clearTimer()
    this._renderPlayback()
  }

  _scheduleStep() {
    this._clearTimer()
    const interval = Math.max(STEP_INTERVAL_FLOOR_MS, this.stepIntervalValue)
    this._timer = setTimeout(() => {
      if (!this._playing) return
      // The story STOPS at the last chapter (no wrap) — replaying is one
      // click on Play from chapter 1 via prev/first.
      if (this._index >= this._views.length - 1) { this.pause(); return }
      this._goToChapter(this._index + 1)
      this._scheduleStep()
    }, interval)
  }

  _clearTimer() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null }
  }

  // ── Beat + shareable moment (#view= / #beat=) ──────────

  // The beat is the user's single-node selection — any node, not just focus
  // members (a multi-node or empty selection clears it). Only THIS canvas's
  // selectionchange counts: the event target is the engine element.
  _handleSelectionChange(event) {
    if (!this._flow || event.target !== this._flow.element) return
    const nodes = event.detail?.nodes || []
    const beat = nodes.length === 1 ? `${nodes[0]}` : null
    if (beat === this._beatNodeId) return
    this._beatNodeId = beat
    this._renderBeat()
    this._writeHash()
  }

  copyMoment() {
    this._writeHash() // the address bar IS the payload — make sure it is current
    const url = window.location.href
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => this._setStatus("Link copied"),
        () => this._setStatus(url) // clipboard denied — surface the URL for a manual copy
      )
    } else {
      this._setStatus(url)
    }
  }

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  // Story OWNS #view=/#beat= (see the header ownership table) — both are
  // written with history.replaceState only, and every other hash param
  // (#focus=, #route=, #lens=, host params) survives untouched.
  _writeHash() {
    if (this._views.length === 0) return
    const viewId = this._views[this._index]?.id
    if (!viewId) return
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    params.set("view", `${viewId}`)
    if (this._beatNodeId) params.set("beat", this._beatNodeId)
    else params.delete("beat")
    const hash = params.toString()
    const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`
    window.history.replaceState(null, "", url)
  }

  // Deep-linked moment restore: #view=<id> jumps straight to that chapter
  // (instant, not animated — a refresh must LAND, not glide) and #beat=
  // re-selects the beat node (its selectionchange then rewrites the hash).
  // Unknown ids fall back to the first chapter. Deferred ONE frame: the
  // engine's own init fitView (fit_view_on_init) is scheduled on a rAF
  // during ITS connect, which ran BEFORE ours — applying the chapter camera
  // on the next frame lands after it and wins instead of being stomped by
  // the init fit-all.
  _bootFromHash() {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    const viewId = params.get("view")
    let index = 0
    if (viewId) {
      const found = this._views.findIndex((view) => `${view.id}` === viewId)
      if (found >= 0) index = found
    }
    requestAnimationFrame(() => {
      this._goToChapter(index, { animate: false })
      const beat = params.get("beat")
      if (beat && this._flow.getNode(beat)) this._flow.selectNode(beat)
    })
  }
}
